import React from "react";
import {
  isCollapsedBetween,
  isNodeSpaced
} from "./../../ricos-common-plugins/gap-spacer/gap-spacer-nodes.js";
import { Node_Type } from "./../../ricos-schema/legacy/wix/rich_content/v1/index.js";
import { GapSpacer } from "./../../ricos-viewer-next/ContentViewer/components/gap-spacer.js";
const renderGapSpacerBeforeNodeIfNeeded = (node, index, nodeTreeMap) => {
  const nodeId = node.id || `0-${index}`;
  const currentNode = nodeTreeMap[nodeId];
  if (!currentNode || !shouldPlaceGapSpacerBefore(nodeId, nodeTreeMap)) {
    return [];
  }
  const isCollapsed = shouldCollapseGapSpacer(nodeId, nodeTreeMap);
  return [
    /* @__PURE__ */ React.createElement(
      GapSpacer,
      {
        key: `gap-spacer-${currentNode.node?.id || currentNode.index}`,
        collapsed: isCollapsed
      }
    )
  ];
};
const renderGapSpacerAfterLastNodeIfNeeded = (nodes, nodeTreeMap) => {
  if (nodes.length === 0) {
    return null;
  }
  const lastNode = nodes[nodes.length - 1];
  const lastNodeId = lastNode.id || `0-${nodes.length - 1}`;
  const lastNodeData = nodeTreeMap[lastNodeId];
  if (!lastNodeData || !shouldPlaceGapSpacerAfter(lastNodeId, nodeTreeMap)) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(GapSpacer, { key: `gap-spacer-${lastNodeData.node?.id || lastNodeData.index + 1}` });
};
const shouldPlaceGapSpacerBefore = (nodeId, map) => {
  const currentNode = map[nodeId];
  const node = currentNode?.node;
  if (!currentNode || !node || isInlineContent(node)) {
    return false;
  }
  if (currentNode.parent && shouldAvoidSpacingBetweenSiblings(currentNode)) {
    return false;
  }
  if (!currentNode.parent) {
    if (currentNode.index === 0) {
      return isNodeSpaced(node.type);
    }
    const rootNodes = Object.values(map).filter((n) => n.depth === 0);
    const prevSibling = rootNodes.find((n) => n.index === currentNode.index - 1);
    return prevSibling ? isNodeSpaced(prevSibling.node.type) && isNodeSpaced(node.type) : false;
  }
  if (!closedBefore(nodeId, map) || !closedAfter(nodeId, map)) return false;
  return true;
};
const shouldPlaceGapSpacerAfter = (nodeId, map) => {
  const currentNode = map[nodeId];
  const node = currentNode?.node;
  if (!currentNode || !node || isInlineContent(node)) {
    return false;
  }
  if (isTextBlock(node)) {
    return false;
  }
  if (currentNode.parent && shouldAvoidSpacingBetweenSiblings(currentNode)) {
    return false;
  }
  return closedAfter(nodeId, map);
};
const isInlineContent = (node) => {
  return node.type === Node_Type.TEXT;
};
const isTextBlock = (node) => {
  return [
    Node_Type.PARAGRAPH,
    Node_Type.HEADING,
    Node_Type.BLOCKQUOTE,
    Node_Type.CODE_BLOCK
  ].includes(node.type);
};
const shouldAvoidSpacingBetweenSiblings = (currentNode) => {
  const parent = currentNode.parent;
  if (!parent) return false;
  if (parent.node.type === Node_Type.TABLE_ROW && currentNode.node.type === Node_Type.TABLE_CELL) {
    return true;
  }
  if (parent.node.type === Node_Type.TABLE && currentNode.node.type === Node_Type.TABLE_ROW) {
    return true;
  }
  if (parent.node.type === Node_Type.LAYOUT && currentNode.node.type === Node_Type.LAYOUT_CELL) {
    return true;
  }
  if (parent.node.type === Node_Type.COLLAPSIBLE_LIST && currentNode.node.type === Node_Type.COLLAPSIBLE_ITEM) {
    return true;
  }
  if (parent.node.type === Node_Type.COLLAPSIBLE_ITEM && (currentNode.node.type === Node_Type.COLLAPSIBLE_ITEM_TITLE || currentNode.node.type === Node_Type.COLLAPSIBLE_ITEM_BODY)) {
    return true;
  }
  if ((parent.node.type === Node_Type.BULLETED_LIST || parent.node.type === Node_Type.ORDERED_LIST) && currentNode.node.type === Node_Type.LIST_ITEM) {
    return true;
  }
  if (parent.node.type === Node_Type.LIST_ITEM && (currentNode.node.type === Node_Type.BULLETED_LIST || currentNode.node.type === Node_Type.ORDERED_LIST)) {
    return true;
  }
  return false;
};
const closedBefore = (nodeId, map) => {
  const startNode = map[nodeId];
  if (!startNode) return false;
  if (startNode.depth === 0) {
    if (startNode.index === 0) {
      return isNodeSpaced(startNode.node.type);
    }
    const rootNodes = Object.values(map).filter((n) => n.depth === 0);
    const prevSibling = rootNodes.find((n) => n.index === startNode.index - 1);
    return prevSibling ? isNodeSpaced(prevSibling.node.type) : false;
  }
  if (isTextBlock(startNode.node)) {
    return false;
  }
  for (let d = startNode.depth; d >= 0; d--) {
    let nodeAtDepth = startNode;
    while (nodeAtDepth && nodeAtDepth.depth > d) {
      nodeAtDepth = nodeAtDepth.parent;
    }
    if (!nodeAtDepth || nodeAtDepth.depth !== d) continue;
    const index = nodeAtDepth.index;
    const parent = nodeAtDepth.parent;
    if (index === 0) {
      if (parent && isNodeSpaced(parent.node.type)) {
        return true;
      }
      continue;
    }
    if (parent && parent.children[index - 1]) {
      let before = parent.children[index - 1];
      while (before.children.length > 0) {
        before = before.children[before.children.length - 1];
      }
      if (before.children.length === 0 && !isInlineContent(before.node)) {
        return isNodeSpaced(before.node.type);
      }
      if (isNodeSpaced(before.node.type)) {
        return true;
      }
      if (isInlineContent(before.node)) {
        return false;
      }
    }
  }
  return false;
};
const closedAfter = (nodeId, map) => {
  const startNode = map[nodeId];
  if (!startNode) return false;
  let currentNode = startNode;
  for (let d = startNode.depth; d >= 0; d--) {
    const parent = currentNode.parent;
    const indexAfter = currentNode.index + 1;
    if (!parent || indexAfter >= parent.children.length) {
      if (d === 0) {
        return isNodeSpaced(currentNode.node.type);
      }
      if (parent && isNodeSpaced(parent.node.type)) {
        return true;
      }
      if (!parent) break;
      currentNode = parent;
      continue;
    }
    let after = parent.children[indexAfter];
    while (after.children.length > 0) {
      after = after.children[0];
    }
    if (after.children.length === 0 && !isInlineContent(after.node)) {
      return isNodeSpaced(after.node.type);
    }
    if (isNodeSpaced(after.node.type)) {
      return true;
    }
    if (isInlineContent(after.node)) {
      return false;
    }
    if (!parent) break;
    currentNode = parent;
  }
  return false;
};
const shouldCollapseGapSpacer = (nodeId, map) => {
  const currentNode = map[nodeId];
  const prevNode = currentNode.parent ? currentNode.parent.children[currentNode.index - 1] : Object.values(map).find((n) => n.depth === 0 && n.index === currentNode.index - 1);
  if (!currentNode || !prevNode) {
    return false;
  }
  return isCollapsedBetween(prevNode.node.type, currentNode.node.type);
};
export {
  renderGapSpacerAfterLastNodeIfNeeded,
  renderGapSpacerBeforeNodeIfNeeded
};
//# sourceMappingURL=gap-spacer-viewer.js.map
